import React, { useEffect, useMemo, useState } from 'react';

import { getUserInfo, getUserManagementBulkFeatureFlags, updateUserManagementBulkFeatureFlags } from 'apis/UserAPI';
import { useRootContext } from 'components/layout/RootContext';
import Modal from 'components/lib/Modal';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleUpdateUser } from 'store/slices/authSlice';

import { getUpdatedBulkFeatureFlagChanges, hasUserManagementFeatureFlagChanges } from './helpers';
import {
  UserManagementFeatureFlagKeyType,
  UserManagementFeatureFlagOptionType,
  UserManagementFeatureFlagsType,
  UserManagementUserType,
} from './types';
import UserManagementFeatureFlagsModalBulkList from './UserManagementFeatureFlagsModalBulkList';
import UserManagementFeatureFlagsModalContent from './UserManagementFeatureFlagsModalContent';
import UserManagementFeatureFlagsModalFooter from './UserManagementFeatureFlagsModalFooter';
import UserManagementFeatureFlagsModalHeader from './UserManagementFeatureFlagsModalHeader';
import UserManagementFeatureFlagsModalStatus from './UserManagementFeatureFlagsModalStatus';
import { STATUS } from '../../constants';

type Props = {
  open: boolean;
  onClose: () => void;
  users: UserManagementUserType[];
  onSaved?: () => void;
};

const UserManagementMultipleUsersFeatureFlagsModal = ({ open, onClose, users, onSaved }: Props) => {
  const dispatch = useAppDispatch();
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);

  const [status, setStatus] = useState(STATUS.IDLE);
  const [saveStatus, setSaveStatus] = useState(STATUS.IDLE);
  const [availableFlags, setAvailableFlags] = useState<UserManagementFeatureFlagOptionType[]>([]);
  const [bulkFeatureFlagChanges, setBulkFeatureFlagChanges] = useState<Partial<UserManagementFeatureFlagsType>>({});

  const userIds = useMemo(() => users.map((selectedUser) => selectedUser.id), [users]);
  const userIdsKey = userIds.join(',');
  const selectedUsersCount = users.length;

  useEffect(() => {
    if (!open) {
      setStatus(STATUS.IDLE);
      setSaveStatus(STATUS.IDLE);
      setAvailableFlags([]);
      setBulkFeatureFlagChanges({});
      return;
    }

    let isCancelled = false;

    const loadFeatureFlags = async () => {
      try {
        setStatus(STATUS.LOADING);

        const nextAvailableFlags = await getUserManagementBulkFeatureFlags(userIds);

        if (isCancelled) {
          return;
        }

        setAvailableFlags(nextAvailableFlags);
        setBulkFeatureFlagChanges({});
        setStatus(STATUS.LOADED);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setStatus(STATUS.FAILURE);
        showMessage('error', 'Failed to load feature flags');
        console.error('Failed to load feature flags', error);
      }
    };

    loadFeatureFlags();

    return () => {
      isCancelled = true;
    };
  }, [open, showMessage, userIds, userIdsKey]);

  const hasChanges = useMemo(
    () => hasUserManagementFeatureFlagChanges(true, bulkFeatureFlagChanges, null, null),
    [bulkFeatureFlagChanges]
  );

  const handleBulkFlagChange = (key: UserManagementFeatureFlagKeyType, checked: boolean) => {
    setBulkFeatureFlagChanges({
      ...bulkFeatureFlagChanges,
      [key]: checked,
    });
  };

  const handleBulkFlagOptionClick = (key: UserManagementFeatureFlagKeyType, value: boolean) => {
    setBulkFeatureFlagChanges(getUpdatedBulkFeatureFlagChanges(bulkFeatureFlagChanges, key, value));
  };

  const handleSave = async () => {
    try {
      setSaveStatus(STATUS.LOADING);

      await updateUserManagementBulkFeatureFlags({
        user_ids: userIds,
        feature_flags: bulkFeatureFlagChanges,
      });

      if (users.some((selectedUser) => currentUser?.PAUser_Id === selectedUser.id)) {
        const updatedCurrentUser = await getUserInfo();
        dispatch(handleUpdateUser(updatedCurrentUser));
      }

      showMessage('success', 'Feature flags updated!');
      onSaved?.();
      onClose();
    } catch (error) {
      showMessage('error', 'Failed to update feature flags');
      console.error('Failed to update feature flags', error);
    } finally {
      setSaveStatus(STATUS.IDLE);
    }
  };

  const isLoading = status === STATUS.LOADING;
  const isSaving = saveStatus === STATUS.LOADING;

  return (
    <Modal
      title={<UserManagementFeatureFlagsModalHeader isBulkMode selectedUsersCount={selectedUsersCount} />}
      open={open}
      centered
      width={620}
      destroyOnClose
      onCancel={onClose}
      footer={
        <UserManagementFeatureFlagsModalFooter
          hasChanges={hasChanges}
          isLoading={isLoading}
          isSaving={isSaving}
          onClose={onClose}
          onSave={handleSave}
        />
      }
    >
      <UserManagementFeatureFlagsModalContent>
        <UserManagementFeatureFlagsModalStatus
          availableFlagsCount={availableFlags.length}
          isLoading={isLoading}
          status={status}
        />
        {!isLoading && status !== STATUS.FAILURE && availableFlags.length > 0 && (
          <UserManagementFeatureFlagsModalBulkList
            availableFlags={availableFlags}
            bulkFeatureFlagChanges={bulkFeatureFlagChanges}
            selectedUsersCount={selectedUsersCount}
            onBulkFlagChange={handleBulkFlagChange}
            onBulkFlagOptionClick={handleBulkFlagOptionClick}
          />
        )}
      </UserManagementFeatureFlagsModalContent>
    </Modal>
  );
};

export default UserManagementMultipleUsersFeatureFlagsModal;
